import {
    parsePhoneNumberFromString,
} from 'libphonenumber-js';

/**
 * Génère toutes les variantes possibles d’un numéro de téléphone basé sur le format UE.
 */
export function getAllFormatedPhoneNumber (phoneNumber: string): string[] {
    if (!phoneNumber) return [];

    const euCountryCodes = [
        '32','33','34','39','30','31','36','40','41','43','44','45','46','47','48','49',
        '351','352','353','354','355','356','357','358','359','370','371','372','373',
        '374','375','376','377','378','380','381','382','385','386','387','389','420','421'
    ];

    // Nettoyage de base
    let num = phoneNumber.trim();
    // Remplacer 00 en + si présent
    if (num.startsWith('00')) num = '+' + num.slice(2);
    // garder uniquement chiffres et + en début
    num = num.replace(/[^\d+]/g, '');

    // Retirer le + pour travailler sur les chiffres mais garder indicateur s'il existait
    const hasPlus = num.startsWith('+');
    const digits = hasPlus ? num.slice(1) : num;

    // Chercher le code pays correspondant (prendre le plus long match possible)
    let matchedCode: string | null = null;
    for (const code of euCountryCodes.sort((a, b) => b.length - a.length)) {
        if (digits.startsWith(code)) {
            matchedCode = code;
            break;
        }
    }

    // national: ce qui suit le code pays si on en a trouvé un, sinon tout le digits
    let national = matchedCode ? digits.slice(matchedCode.length) : digits;
    // conservez une version avec le(s) 0 initiaux (si présents) et une version sans les 0 initiaux
    const nationalWithTrunk = national; // p.ex. "0672954423" si présentait '0'
    const stripped = national.replace(/^0+/, ''); // "672954423"

    const formats = new Set<string>();

    // Si on a trouvé un code pays -> générer variantes pour ce code
    if (matchedCode) {
        // formes principales
        formats.add(`+${matchedCode}${stripped}`);   // +33 6...
        formats.add(`+${matchedCode}0${stripped}`);  // +33 06... (la variante demandée)
        formats.add(`00${matchedCode}${stripped}`);  // 0033 6...
        formats.add(`00${matchedCode}0${stripped}`); // 0033 06...
        formats.add(`${matchedCode}${stripped}`);    // 33 6...
        formats.add(`0${stripped}`);                 // national trunk (06...)
        // versions sans +
        formats.add(`${matchedCode}${stripped}`);
    } else {
        // Pas de code trouvé : générer pour tous les codes UE en supposant que l'utilisateur
        // a donné un numéro national (ex: "0672954423") -> on enlève les 0 initiaux et teste pour toutes
        const baseStripped = stripped || nationalWithTrunk; // si tout a été enlevé, fallback
        for (const code of euCountryCodes) {
            formats.add(`+${code}${baseStripped}`);
            formats.add(`+${code}0${baseStripped}`);
            formats.add(`00${code}${baseStripped}`);
            formats.add(`${code}${baseStripped}`);
        }
        // ajouter aussi les formes locales possibles
        formats.add(nationalWithTrunk);
        formats.add(baseStripped);
    }

    // Ajouter le numéro d'entrée nettoyé brut (avec ou sans +)
    formats.add(num);
    if (hasPlus) formats.add(num.slice(1));

    return Array.from(formats);
}


const DEFAULT_COUNTRY = 'FR';

/**
 * Normalise un numéro de téléphone en E.164
 * - Détecte automatiquement le pays si possible
 * - Corrige les erreurs FR courantes (+330, 00330, etc.)
 * - Fallback FR si impossible à détecter
 */
export function normalizePhoneNumber (
    phone?: string
): string | undefined {
    if (!phone) return undefined;

    // 1. Nettoyage de base
    let raw = phone
        .trim()
        .replace(/[^\d+]/g, '');

    // 2. Corriger les erreurs françaises courantes
    // +3306xxxxxx -> +336xxxxxx
    raw = raw.replace(/^\+330/, '+33');
    raw = raw.replace(/^00330/, '0033');

    // 3. Si commence par 00 → +
    if (raw.startsWith('00')) {
        raw = '+' + raw.slice(2);
    }

    // 4. Tentative de parsing AVEC indicatif
    let phoneNumber =
        parsePhoneNumberFromString(raw) ??
        parsePhoneNumberFromString(raw, DEFAULT_COUNTRY);

    if (!phoneNumber || !phoneNumber.isValid()) {
        return undefined;
    }

    return phoneNumber.format('E.164');
}
